import type { ComponentType, ReactNode } from 'react';
import type { FieldChangeScope } from '@alliance-strategies/form-engine-portable';
import type { PassportRepairedApi } from './passportRepairedContract';

/**
 * Runtime helpers available in the currently published form-engine-portable
 * artifact. The custom passport adapter uses the published 2.46 public API.
 */
export interface FormEngineHelpers {
  getInitialFormValues?: (
    config: unknown,
    progress: unknown,
    auto: unknown,
    stepKey?: string,
    behaviors?: unknown,
  ) => Record<string, unknown>;
  createEmptyRegistry?: () => unknown;
  applyPassportExtraction?: (form: unknown, data: unknown) => string[];
  passportApi?: PassportRepairedApi<FieldChangeScope>;
  normalizeLegalName?: (value: unknown) => string;
  passportExtractionFieldMap?: Record<string, string>;
  FormEngineAPProvider?: ComponentType<{ value: unknown; children: ReactNode }>;
  formEngineDefaultContext: Record<string, unknown>;
}

/** Load supported engine exports from the same lazy chunk as the renderer. */
export async function engineHelpers(): Promise<FormEngineHelpers> {
  const m: any = await import('@alliance-strategies/form-engine-portable');
  return {
    getInitialFormValues: m.getInitialFormValues,
    createEmptyRegistry: m.createEmptyRegistry,
    applyPassportExtraction: m.applyPassportExtraction,
    passportApi: {
      applyPassportExtraction: m.applyPassportExtraction,
      collectUploadReferenceTokens: m.collectUploadReferenceTokens,
      isUploadReferenceCurrent: m.isUploadReferenceCurrent,
    },
    normalizeLegalName: m.normalizeLegalName,
    passportExtractionFieldMap: m.PASSPORT_EXTRACTION_FIELD_MAP,
    FormEngineAPProvider: m.FormEngineAPProvider,
    formEngineDefaultContext: m.formEngineDefaultContext ?? {},
  };
}

const PASSPORT_EXTRACTION_FIELD_MAP_FALLBACK: Record<string, string> = {
  givenNames: 'passport_name',
  surname: 'passport_surname',
  passportNumber: 'passport_number',
  nationality: 'passport_nationality',
  issuingCountry: 'passport_country',
  placeOfBirth: 'passport_place_of_birth',
  dateOfBirth: 'passport_date_of_birth',
  dateOfIssue: 'passport_issue_date',
  dateOfExpiry: 'passport_expiry_date',
};

/** Form field an OCR payload key would write in the installed engine. */
export function passportOcrFieldFor(helpers: FormEngineHelpers, key: string): string | undefined {
  return helpers.passportExtractionFieldMap?.[key] ?? PASSPORT_EXTRACTION_FIELD_MAP_FALLBACK[key];
}
